"use client";

import { MutableRefObject, useMemo, useState } from "react";

import { Hbml } from "@holibob-packages/hbml";
import { sanitizeHbml } from "@holibob-packages/hbml";
import { Language } from "@holibob-packages/language";
import { AssetUrl, VaultUrl } from "@holibob-packages/vault";

import { OnBookingSubmitProps } from ".";
import type { OnBookingCompletedCallback, ProductData } from "../apiHooks";
import { ErrorBoundary } from "../components";
import { ProductContentEntryFragment } from "../gql-request";
import { ProductStartPlaceFragment } from "../gql-request";
import { useLanguage } from "../hooks/useLanguage";
import {
    ProductFullV2 as ProductFullComponentV2,
    ProductV2Props as ProductFullComponentProps,
} from "../product/V2/ProductFullV2";
import { ProductContentEntryV2, ProductContentV2, ProductMeta as ProductMetaType } from "../product/types";
import { Price } from "../types/Price";
import { ProductContentType } from "../types/ProductContent";
import { BookingConsumerTripInput } from "../types/ProductFullLoaded";
import { durationFromString } from "../utils/formatters/formatDuration";
import { CheckAvailabilityButtonV2 } from "./Availability";
import { AvailabilityContent } from "./Availability/V2/AvailabilityContent";

export type ProductFullLoadedV2Props = {
    product: ProductData;
    bookingReference?: string;
    onBookingCompleted?: OnBookingCompletedCallback;
    onAvailabilityChange?: (availability: string | null) => void;
    showHeader?: boolean;
    constrainWidth?: boolean;
    showAvailability?: boolean;
    showAvailabilityModal?: boolean;
    withStickyAppBar?: boolean;
    withSectionFadeUpAnimation?: boolean;
    startPlace?: ProductStartPlaceFragment;
    showFavouriteButton?: boolean;
    isFavourite?: boolean;
    onFavouriteChange?: (isFavourite: boolean) => void;
    showOperatedBy: boolean;
    showSourcedFrom: boolean;
    consumerTrip?: BookingConsumerTripInput;
    onCheckAvailability?: (product: ProductData) => void;
    scrollableContainerRef?: MutableRefObject<HTMLDivElement | null>;
    submitButtonLabel?: string;
    onBookingSubmit?: (props: OnBookingSubmitProps) => Promise<void>;
} & Pick<ProductFullComponentProps, "breadcrumbs" | "showNavigator" | "mode"> & { children?: React.ReactNode };

export function ProductFullLoadedV2({
    product,
    onBookingCompleted,
    breadcrumbs,
    bookingReference,
    onAvailabilityChange,
    showHeader = true,
    constrainWidth = true,
    showAvailability = true,
    showAvailabilityModal = true,
    withStickyAppBar = false,
    withSectionFadeUpAnimation,
    showFavouriteButton,
    isFavourite,
    onFavouriteChange,
    showOperatedBy,
    showSourcedFrom,
    consumerTrip,
    onCheckAvailability,
    scrollableContainerRef,
    submitButtonLabel,
    onBookingSubmit,
    children,
    showNavigator = true,
    mode,
}: ProductFullLoadedV2Props) {
    const language = useLanguage();
    const content = useMemo(() => {
        return createProductContent(product);
    }, [product]);
    const [isAvailabilityModelOpen, setIsAvailabilityModelOpen] = useState(false);
    const [availability, setAvailability] = useState<string | null>("none");

    const meta = useMemo(() => {
        return createProductMeta(product, language);
    }, [product, language]);

    const availabilityChangeHandler = (availability: string | null | undefined = null) => {
        setAvailability(availability);
        onAvailabilityChange?.(availability);
    };

    const availabilitySection = showAvailability ? (
        <AvailabilitySection
            product={product}
            availability={availability}
            onContentTrigger={() => {
                setIsAvailabilityModelOpen(true);
                onCheckAvailability?.(product);
            }}
        />
    ) : null;

    const imageList = product.images.map((x) => {
        const vaultUrl = VaultUrl.fromString(x.urlLarge).unwrap();
        return new AssetUrl({
            type: "productImage",
            id: vaultUrl.key.id,
        });
    });
    const minPrice: Price | undefined = product.holibobGuidePrice
        ? {
              amount: product.holibobGuidePrice.gross,
              currency: product.holibobGuidePrice.currency,
          }
        : undefined;

    const productReview = {
        reviewList: product.reviewList.nodes,
        reviewSummary: product.reviewList.summary,
        pageCount: product.reviewList.pageCount,
        recordCount: product.reviewList.recordCount,
    };

    return (
        <>
            <ProductFullComponentV2
                productId={product.id}
                title={product.name ?? ""}
                imageList={imageList}
                minPrice={minPrice}
                content={content}
                breadcrumbs={breadcrumbs}
                meta={meta}
                attributeList={product.attributeList.nodes}
                additions={product.additionList.nodes}
                cancellationPolicy={product.cancellationPolicy}
                reviewRating={product.reviewRating}
                reviewCount={product.reviewCount}
                showHeader={showHeader}
                showNavigator={showNavigator}
                constrainWidth={constrainWidth}
                withStickyAppBar={withStickyAppBar}
                minDuration={product.minDuration}
                maxDuration={product.maxDuration}
                guidePriceType={product.guidePriceType}
                availability={availabilitySection}
                withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                itineraryItems={product.itinerary.itemList.nodes}
                guidePricePricingData={product.holibobGuidePrice?.pricingData}
                startPlace={product.startPlace}
                provider={product.provider}
                sourceInstance={product.sourceInstance}
                productReview={productReview}
                showFavouriteButton={showFavouriteButton}
                isFavourite={isFavourite}
                onFavouriteChange={onFavouriteChange}
                showOperatedBy={showOperatedBy}
                showSourcedFrom={showSourcedFrom}
                scrollableContainerRef={scrollableContainerRef}
                mode={mode}
            />
            {children}
            <AvailabilityContent
                product={product}
                bookingReference={bookingReference}
                onBookingCompleted={onBookingCompleted}
                onAvailabilityChange={availabilityChangeHandler}
                consumerTrip={consumerTrip}
                open={showAvailabilityModal && isAvailabilityModelOpen}
                onContentClose={setIsAvailabilityModelOpen}
                submitButtonLabel={submitButtonLabel}
                onBookingSubmit={onBookingSubmit}
            />
        </>
    );
}

type AvailabilitySectionProps = {
    product: ProductData;
    availability?: string | null;
    onContentTrigger: () => void;
};
const AvailabilitySection = (props: AvailabilitySectionProps) => {
    const { product, onContentTrigger, availability } = props;

    return (
        <ErrorBoundary>
            <CheckAvailabilityButtonV2
                product={product}
                onContentTrigger={onContentTrigger}
                availability={availability}
            />
        </ErrorBoundary>
    );
};

function createProductContent(product: ProductData): ProductContentV2 {
    return {
        root: product.description
            ? { name: product.name, descriptionJson: sanitizeHbml(product.descriptionJson) }
            : undefined,
        other: computeOtherProductContent(product.contentList.nodes),
    };
}

function computeOtherProductContent(productContentNodes: ReadonlyArray<ProductContentEntryFragment>) {
    const nodes = productContentNodes.slice().sort((a, b) => {
        return (a.ordinalPosition ?? 0) - (b.ordinalPosition ?? 0);
    });

    const other = new Map<ProductContentType, ProductContentEntryV2[]>();
    for (const entry of nodes) {
        const productContentEntry = computeProductContentEntry(entry);

        if (!productContentEntry) {
            continue;
        }

        const entryType = entry.type as ProductContentType;
        let tab = other.get(entryType);
        if (!tab) {
            other.set(entryType, (tab = []));
        }
        tab.push(productContentEntry);
    }

    return other;
}

function computeProductContentEntry(node: ProductContentEntryFragment): ProductContentEntryV2 | undefined {
    const descriptionJson: Hbml = node.descriptionJson ? JSON.parse(node.descriptionJson) : [];
    const name = node.name ?? "";
    if (!name && descriptionJson.length === 0) {
        return;
    }

    return {
        name,
        descriptionJson: sanitizeHbml(descriptionJson),
        isManual: !node.synchronisationId,
    };
}

function optionalTransform<T>(value: string | undefined, transform: (value: string) => T): T | undefined {
    if (value) {
        return transform(value);
    }
    return;
}

function createProductMeta(product: ProductData, language: Language): ProductMetaType {
    const metaIndexed = product.metaList.nodes.reduce((acc, node) => {
        if (node.type && node.value) {
            acc.set(node.type, node.value);
        }
        return acc;
    }, new Map<string, string>());

    const walkingDistance = optionalTransform(metaIndexed.get("WALKING_DISTANCE_KILOMETERS"), (x) => {
        const result = parseFloat(x);
        if (Number.isNaN(result)) {
            return;
        }
        return result;
    });

    return {
        minDuration: product.minDuration,
        maxDuration: product.maxDuration,
        startTime: metaIndexed.get("START_TIME"),
        fitnessLevel: metaIndexed.get("FITNESS_LEVEL"),
        difficultyLevel: metaIndexed.get("DIFFICULTY_LEVEL"),
        walkingDistance,
        cultureShockLevel: product.cultureShockLevel,
        bestTimeToVisit: product.bestTimeToVisit,
        maxGroupSize: product.maxGroupSize,
        guideLanguages: product.guideLanguageList.nodes,
        advanceArrivalDuration: product.advanceArrivalDuration
            ? durationFromString(language, product.advanceArrivalDuration)
            : undefined,
        startTimes: product.startTimeList.nodes,
    };
}
